import React, {useState} from 'react';
import {toast} from 'react-toastify';
import {DirectionalHint, Icon, Spinner, SpinnerSize, Text, TooltipHost, useTheme} from '@fluentui/react';
import {useGetProductPurchaseHistoryQuery} from '../../store/Api';
import {useLazyResolveRecordUrlQuery} from '../../store/endpoints/RecordLinksEndpoints';
import {openEntity} from '../GlobalSearch/useGlobalSearchHotkey';
import {IProductPurchaseStats} from '../../models/Search';
import {IPurchaseHistoryItem} from '../../models/Extension';

interface IPurchaseStatsPeekProps {
    productId?: string;
    catalog?: string;
    stats?: IProductPurchaseStats;
    children: React.ReactElement;
}

const MAX_ROWS = 6;
const MONEY = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 2});

const sourceIcon = (item: IPurchaseHistoryItem): string =>
    item.sourceType === 'storefront' ? 'ShoppingCart' : item.sourceType === 'opportunity' ? 'Handshake' : 'Link';

const formatDate = (d: string | null): string => {
    if (!d) return '';
    const date = new Date(d);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

/**
 * Hover flyout on the Ordered cell: the most recent purchases behind the count, from the same
 * endpoint the Purchase History tab reads (storefront orders and imported CRM history merged),
 * loaded on first hover. Clicking the cell still opens the full tab.
 */
const PurchaseStatsPeek: React.FC<IPurchaseStatsPeekProps> = ({productId, catalog, stats, children}) => {
    const theme = useTheme();
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();
    const [peeked, setPeeked] = useState(false);

    // Where a line can go: a storefront order opens in the order panel right here (the same entity host the
    // omnibox and the grid's product clicks use, so the search stays put); a CRM opportunity resolves a live
    // deep link through its folio and opens there (same flow as the Purchase History tab).
    const linkOf = (item: IPurchaseHistoryItem): (() => void) | undefined => {
        if (item.storefrontOrderId) {
            const orderId = item.storefrontOrderId;
            return () => openEntity({kind: 'order', orderId});
        }
        if (item.folioId && item.opportunityId) {
            const folioId = item.folioId, opportunityId = item.opportunityId;
            return () => {
                const toastId = toast.loading('Opening record in the CRM…');
                resolveRecordUrl({folioId, recordType: 'Opportunity', recordId: opportunityId}).unwrap()
                    .then((res) => {
                        if (!res.supported) toast.update(toastId, {type: 'error', render: "This record can't be opened in the CRM", isLoading: false, autoClose: 4000});
                        else if (res.success && res.url) { window.open(res.url, '_blank', 'noopener'); toast.dismiss(toastId); }
                        else toast.update(toastId, {type: 'error', render: res.error?.trim() || 'Could not open record in the CRM', isLoading: false, autoClose: 5000});
                    })
                    .catch(() => toast.update(toastId, {type: 'error', render: 'Failed to open record', isLoading: false, autoClose: 4000}));
            };
        }
        return undefined;
    };
    const canPeek = !!productId && !!catalog && !!stats?.orderCount;
    const history = useGetProductPurchaseHistoryQuery(
        {catalog: catalog ?? '', productId: productId ?? ''},
        {skip: !peeked || !canPeek}
    );
    if (!canPeek) return children;

    const items = history.data?.items ?? [];
    const shown = items.slice(0, MAX_ROWS);
    const more = (history.data?.total ?? items.length) - shown.length;
    const windowLabel = stats?.windowMonths ? `the last ${stats.windowMonths} months` : 'all time';
    // The history lists every purchase; the count only covers the window. Lines past the cutoff are shown
    // muted so the two visibly reconcile.
    const cutoff = stats?.windowMonths ? new Date(new Date().setMonth(new Date().getMonth() - stats.windowMonths)) : undefined;
    const outsideWindow = (item: IPurchaseHistoryItem) => !!cutoff && !!item.date && new Date(item.date) < cutoff;
    const olderCount = cutoff ? items.filter(outsideWindow).length : 0;

    const content = (
        <div style={{minWidth: 300, maxWidth: 480}}>
            <Text variant='mediumPlus' block styles={{root: {fontWeight: 600, marginBottom: 2}}}>
                {stats!.orderCount.toLocaleString()} {stats!.orderCount === 1 ? 'order' : 'orders'}, {stats!.quantityOrdered.toLocaleString()} units
            </Text>
            <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary, marginBottom: 6}}}>
                Counted over {windowLabel}{stats?.lastOrdered ? `; last ordered ${formatDate(stats.lastOrdered)}` : ''}
            </Text>
            {history.isFetching && <Spinner size={SpinnerSize.xSmall} label='Loading purchases…' labelPosition='right' />}
            {!history.isFetching && history.data && shown.length === 0 && (
                <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary}}}>No purchase lines found.</Text>
            )}
            {shown.length > 0 && (
                <table style={{borderCollapse: 'collapse', width: '100%'}}>
                    <tbody>
                        {shown.map((item, i) => {
                            const open = linkOf(item);
                            return (
                            <tr key={`${item.storefrontOrderId ?? item.opportunityId ?? i}-${i}`}
                                className={open ? 'purchase-peek-link' : undefined}
                                style={{lineHeight: '18px', cursor: open ? 'pointer' : undefined, opacity: outsideWindow(item) ? 0.5 : 1}}
                                title={[outsideWindow(item) ? `Before the ${stats?.windowMonths}-month window; not counted` : null, open ? (item.storefrontOrderId ? 'Open the order' : 'Open the opportunity in the CRM') : null].filter(Boolean).join('. ') || undefined}
                                // The flyout is portaled, but React still bubbles to the cell, whose click opens the details tab.
                                onClick={open ? (e) => { e.stopPropagation(); open(); } : undefined}>
                                <td style={{padding: '1px 6px 1px 0', whiteSpace: 'nowrap', color: theme.palette.neutralSecondary}}>{formatDate(item.date)}</td>
                                <td style={{padding: '1px 6px', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600}}
                                    title={[item.companyName, item.orderName, item.orderNumber != null ? `Order ${item.orderNumber}` : null, item.salesRep ? `Rep: ${item.salesRep}` : null].filter(Boolean).join(' · ')}>
                                    <Icon iconName={sourceIcon(item)} styles={{root: {fontSize: 10, marginRight: 4, color: theme.palette.themePrimary}}} aria-hidden />
                                    {item.companyName ?? item.orderName ?? (item.orderNumber != null ? `Order ${item.orderNumber}` : 'Unknown company')}
                                </td>
                                <td style={{padding: '1px 6px', textAlign: 'right', whiteSpace: 'nowrap'}}>{item.quantity.toLocaleString()} ×</td>
                                <td style={{padding: '1px 0 1px 6px', textAlign: 'right', whiteSpace: 'nowrap'}}
                                    title={item.revenue != null ? `Line total ${MONEY.format(item.revenue)}` : item.price != null ? `Line total ${MONEY.format(item.price * item.quantity)}` : undefined}>
                                    {item.price != null ? MONEY.format(item.price) : <span style={{color: theme.palette.neutralTertiary}}>—</span>}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            {more > 0 && <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary, marginTop: 4}}}>+{more} more</Text>}
            {olderCount > 0 && (
                <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary, marginTop: 4}}}>
                    {olderCount} of the listed purchases predate the {stats?.windowMonths}-month window and are not in the count.
                </Text>
            )}
            <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary, marginTop: 6}}}>
                {shown.some((it) => linkOf(it)) ? 'Click a line to open its order; click the cell for the full history' : 'Click the cell for the full history'}
            </Text>
        </div>
    );

    return (
        <TooltipHost
            content={content}
            directionalHint={DirectionalHint.leftCenter}
            calloutProps={{gapSpace: 6}}
            // Lets the pointer cross the gap into the flyout so its lines can be clicked.
            closeDelay={200}
            styles={{root: {display: 'block', width: '100%'}}}
            onTooltipToggle={(visible) => { if (visible) setPeeked(true); }}
        >
            {children}
        </TooltipHost>
    );
};

export default PurchaseStatsPeek;
